import { IBackgroundCheckRequestResponse } from "@wingspanhq/background-checks/dist/lib/interfaces";
import { RequirementType } from "@wingspanhq/payments/dist/interfaces";
import { useWSQueries } from "@ws-react-query";
import { useMemo } from "react";
import { QUERY_BACKGROUND_CHECKS_REQUEST } from "../../../../query/backgroundChecks/queries/useQueryBackgroundChecksRequest";
import { getBackgroundChecksRequest } from "../../../../services/backgroundChecks/getBackgroundChecksRequest";
import { getDataSourceQueries } from "../../helpers/getDataSourceQueries";
import {
  RequirementTableCallback,
  RequirementTableItemData,
  RequirementTableRowConfig
} from "../../types";
import { buildConfigFields } from "../../utils";
import { getBackgroundChecksRequestFromCache } from "./configs/helpers/getBackgroundChecksRequestFromCache";
import { usePayeeBackgroundChecksConfig } from "./configs/payee/usePayeeBackgroundChecksConfig";
import { usePayerBackgroundChecksConfig } from "./configs/payer/usePayerBackgroundChecksConfig";

const useContextQueries = (requirements: RequirementTableItemData[]) => {
  const queries = getDataSourceQueries(
    QUERY_BACKGROUND_CHECKS_REQUEST,
    requirements,
    getBackgroundChecksRequest,
    RequirementType.BackgroundCheck
  );

  return useWSQueries({ queries });
};

export const useBackgroundChecksRequirementTableConfig = (
  callback: RequirementTableCallback,
  requirements: RequirementTableItemData[]
): RequirementTableRowConfig[RequirementType.BackgroundCheck] => {
  const contextQueries = useContextQueries(requirements);
  const payerBackgroundChecksConfig = usePayerBackgroundChecksConfig(callback);
  const payeeBackgroundChecksConfig = usePayeeBackgroundChecksConfig(callback);

  const queriesHash = contextQueries
    .map(query => query.dataUpdatedAt)
    .join(",");

  const fields = useMemo(
    buildConfigFields<
      IBackgroundCheckRequestResponse,
      RequirementType.BackgroundCheck
    >({
      payerConfig: payerBackgroundChecksConfig,
      payeeConfig: payeeBackgroundChecksConfig,
      getAdditionalData: getBackgroundChecksRequestFromCache
    }),
    [queriesHash]
  );

  return {
    ...fields,
    getAdditionalData(requirement, context) {
      return getBackgroundChecksRequestFromCache(requirement, context);
    },
    isLoading: contextQueries.some(query => query.isLoading),
    dataUpdatedAt: queriesHash
  };
};
